<template>
  <b-table
    :items="items"
    :fields="headers"
    :busy="loading"
    :sort-by.sync="localSortBy"
    :sort-desc.sync="localSortDesc"
    :tbody-tr-class="rowClass"
    thead-class="ombudsman-header"
    borderless
    hover
    responsive
    show-empty
    no-local-sorting
    @row-clicked="$emit('row-clicked', $event)"
    @sort-changed="emitSortChanged"
  >
    <template #cell(id)="slotData">
      <span>{{ slotData.item.companyId || slotData.item._id }}</span>
    </template>

    <template #cell(company)="slotData">
      <span class="company-name">{{ slotData.item.company.fantasyName }}</span>
    </template>

    <template #cell(protocol)="slotData">
      <strong class="protocol-link">{{ slotData.item.protocol }}</strong>
    </template>

    <template #cell(person)="slotData">
      <span>{{
        slotData.item.person ? slotData.item.person.fullName : "Anônimo"
      }}</span>
    </template>

    <template #cell(createdAt)="slotData">
      <span class="date-text">{{ slotData.item.createdAt | datetime }}</span>
    </template>

    <template #cell(updatedAt)="slotData">
      <span class="date-text">{{ slotData.item.updatedAt | datetime }}</span>
    </template>

    <template #cell(deadline)="slotData">
      <span class="date-text">{{ getDeadline(slotData.item) | datetime }}</span>
    </template>

    <template #cell(status)="slotData">
      <b-badge :variant="getBadgeColor(slotData.item.status)">
        {{ getStatusText(slotData.item.status) }}
      </b-badge>
    </template>

    <template #cell(snippet)="slotData">
      <span v-html="renderSnippet(slotData.item.text, searchTerm)"></span>
    </template>

    <template #table-busy>
      <div class="text-center py-5">
        <b-spinner></b-spinner>
        <span class="ml-2">Carregando...</span>
      </div>
    </template>
    <template #empty>
      <div class="text-muted text-center py-5">
        <i class="fas fa-check-circle fa-2x"></i>
        <p class="mt-3">Nenhuma ouvidoria encontrada</p>
      </div>
    </template>
  </b-table>
</template>

<script>
import { getBadgeColor, getStatusText } from "../../complaints/utils";
import { getDeadline } from "../ombudsman-utils";

export default {
  name: "OmbudsmanTable",
  props: {
    items: { type: Array, required: true },
    headers: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    sortBy: { type: String, default: null },
    sortDesc: { type: Boolean, default: false },
    searchTerm: { type: String, default: "" },
  },
  data() {
    return {
      localSortBy: this.sortBy,
      localSortDesc: this.sortDesc,
    };
  },
  watch: {
    sortBy(newVal) {
      this.localSortBy = newVal;
    },
    sortDesc(newVal) {
      this.localSortDesc = newVal;
    },
  },
  methods: {
    getBadgeColor,
    getStatusText,
    getDeadline,
    emitSortChanged(sortOptions) {
      this.$emit("sort-changed", sortOptions);
    },
    rowClass(item) {
      if (!item) return "";
      const baseClass = "ombudsman-row";
      switch (parseInt(item.status)) {
        case 0:
          return `${baseClass} status-new`;
        case 2:
          return `${baseClass} status-concluded`;
        default:
          return baseClass;
      }
    },
    renderSnippet(text, searchTerm) {
      if (!text) return '<span class="text-muted">Sem relato</span>';
      if (!searchTerm || !searchTerm.trim()) {
        return text.length > 80 ? text.substring(0, 80) + "..." : text;
      }

      const term = searchTerm.trim().toLowerCase();

      const normalize = (str) =>
        str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();

      const normalizedText = normalize(text);
      const normalizedTerm = normalize(term);

      const index = normalizedText.indexOf(normalizedTerm);
      if (index === -1) {
        return "";
      }

      const start = Math.max(0, index - 10);
      const end = Math.min(text.length, index + term.length + 10);

      let snippet = text.substring(start, end);

      if (start > 0) snippet = "..." + snippet;
      if (end < text.length) snippet = snippet + "...";

      const normalizedSnippet = normalize(snippet);
      const termIndexInSnippet = normalizedSnippet.indexOf(normalizedTerm);
      if (termIndexInSnippet !== -1) {
        const originalWord = snippet.substring(
          termIndexInSnippet,
          termIndexInSnippet + term.length
        );
        const highlighted = `<mark style="background-color: #ffeb3b; padding: 2px 4px; border-radius: 3px; font-weight: bold; color: #212529;">${originalWord}</mark>`;
        snippet =
          snippet.substring(0, termIndexInSnippet) +
          highlighted +
          snippet.substring(termIndexInSnippet + term.length);
      }

      return snippet;
    },
  },
};
</script>

<style scoped>
>>> .ombudsman-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

>>> .ombudsman-header th.sortable:hover {
  color: #0056b3;
}

>>> .ombudsman-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

>>> .ombudsman-row:hover {
  background-color: #e9ecef !important;
}

>>> .ombudsman-row.status-concluded {
  background-color: rgba(40, 167, 69, 0.1);
}

>>> .ombudsman-row.status-new {
  background-color: rgba(0, 123, 255, 0.07);
}

>>> tbody tr:last-of-type {
  border-bottom: none;
}

>>> .ombudsman-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

>>> .protocol-link {
  color: #007bff;
  font-weight: 500;
}

>>> .company-name,
>>> .date-text {
  color: #6c757d;
}
</style>
